/**
 * The e-mail design builder (owner, 2026-09-30): a design is a list of blocks
 * over a theme — header, heading, rich text, highlight box, buttons (the
 * "Start trial" button and any others: text, link and colours all editable),
 * divider, space, image, footer. The preview is the server's own render
 * (email_designs.render via email/design_render), filled with a sample
 * person, so it is exactly what is sent.
 *
 * Used by Email Center → Email designs (the library) and by Campaigns →
 * Design (one campaign's e-mail).
 */
import React, { useEffect, useRef, useState } from 'react';
import { panelPost } from '../../kit';
import { inputCls } from '../emailKit';
import { RichText, RichTextHandle } from './RichText';
import {
  BLOCK_LABELS, Block, BlockType, DesignButton, DesignDoc, Theme, URL_OK, forWire, newBlock, newKey,
} from './types';

const ADDABLE: BlockType[] = ['heading', 'text', 'buttons', 'callout', 'image', 'divider', 'spacer', 'header', 'footer'];
const ALIGNED: BlockType[] = ['header', 'heading', 'text', 'callout', 'buttons', 'image', 'footer'];
const URL_PRESETS = [
  { v: '{upgrade_url}', l: 'Start trial / upgrade (Billing)' },
  { v: '{site_url}', l: 'The site (Cosmos)' },
  { v: 'mailto:info@universitytech.io', l: 'E-mail us' },
];
const RADII = [{ v: 0, l: 'Square' }, { v: 6, l: 'Slightly rounded' }, { v: 12, l: 'Rounded' }, { v: 24, l: 'Pill' }];
const small = 'h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';

type Target = { kind: 'rich'; key: string } | { kind: 'input'; el: HTMLInputElement; apply: (v: string) => void };

const Color: React.FC<{ value?: string; onChange: (v: string) => void; title: string; palette?: string[] }> =
  ({ value = '', onChange, title, palette }) => (
    <span className="inline-flex items-center gap-1" title={title}>
      <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#9A7A4C'}
        onChange={e => onChange(e.target.value.toUpperCase())}
        className="w-7 h-7 rounded border border-grayscale-300 p-0 cursor-pointer" />
      {palette && palette.slice(0, 6).map(p => (
        <button key={p} type="button" onClick={() => onChange(p)} title={p}
          className={`w-4 h-4 rounded-full border ${p.toUpperCase() === value.toUpperCase() ? 'ring-2 ring-primary-500' : 'border-grayscale-300'}`}
          style={{ background: p }} />
      ))}
    </span>
  );

export const DesignBuilder: React.FC<{
  doc: DesignDoc; onChange: (doc: DesignDoc) => void;
  placeholders: { name: string; meaning: string }[];
  fonts: { key: string; label: string }[];
  palette: string[];
  /** When given, the subject is edited here too (and previewed). */
  subject?: string; onSubject?: (s: string) => void;
  maxBlocks?: number; maxButtons?: number;
}> = ({ doc, onChange, placeholders, fonts, palette, subject, onSubject, maxBlocks = 40, maxButtons = 3 }) => {
  const th = doc.theme;
  // Edits read the LATEST doc: a placeholder chip applies through a closure
  // made when the field got focus, and must not undo edits made since.
  const docRef = useRef(doc);
  docRef.current = doc;
  const target = useRef<Target | null>(null);
  const rich = useRef<Record<string, RichTextHandle | null>>({});
  const [preview, setPreview] = useState<{ html: string; text: string; subject: string; unknown: string[] } | null>(null);
  const [err, setErr] = useState('');
  const [width, setWidth] = useState<'desktop' | 'phone'>('desktop');
  const [view, setView] = useState<'html' | 'text'>('html');
  const [addType, setAddType] = useState<BlockType>('text');

  // -- edits ------------------------------------------------------------------
  const setTheme = (p: Partial<Theme>) => {
    const d = docRef.current;
    onChange({ ...d, theme: { ...d.theme, ...p } });
  };
  const patch = (k: string, p: Partial<Block>) => {
    const d = docRef.current;
    onChange({ ...d, blocks: d.blocks.map(b => (b._k === k ? { ...b, ...p } : b)) });
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= doc.blocks.length) return;
    const blocks = [...doc.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    onChange({ ...doc, blocks });
  };
  const remove = (k: string) => onChange({ ...doc, blocks: doc.blocks.filter(b => b._k !== k) });
  const duplicate = (i: number) => {
    const b = doc.blocks[i];
    const copy: Block = { ...b, _k: newKey(), buttons: b.buttons ? b.buttons.map(x => ({ ...x })) : b.buttons };
    onChange({ ...doc, blocks: [...doc.blocks.slice(0, i + 1), copy, ...doc.blocks.slice(i + 1)] });
  };
  const add = () => {
    if (doc.blocks.length >= maxBlocks) return;
    const b = newBlock(addType, th);
    // A footer goes last; anything else goes before an existing footer.
    const fi = doc.blocks.findIndex(x => x.type === 'footer');
    const at = addType === 'footer' || fi < 0 ? doc.blocks.length : fi;
    onChange({ ...doc, blocks: [...doc.blocks.slice(0, at), b, ...doc.blocks.slice(at)] });
  };
  const setButton = (k: string, bi: number, p: Partial<DesignButton>) => {
    const b = docRef.current.blocks.find(x => x._k === k);
    if (!b || !b.buttons) return;
    patch(k, { buttons: b.buttons.map((x, j) => (j === bi ? { ...x, ...p } : x)) });
  };

  // -- placeholders: into whichever field was last in focus ---------------------
  const inputTarget = (apply: (v: string) => void) => (e: React.FocusEvent<HTMLInputElement>) => {
    target.current = { kind: 'input', el: e.currentTarget, apply };
  };
  const insert = (name: string) => {
    const token = `{${name}}`;
    const t = target.current;
    if (!t) return;
    if (t.kind === 'rich') { rich.current[t.key]?.insert(token); return; }
    const el = t.el;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    t.apply(el.value.slice(0, start) + token + el.value.slice(end));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };

  // -- the server's preview -------------------------------------------------------
  const wire = JSON.stringify(forWire(doc));
  useEffect(() => {
    let dead = false;
    const t = setTimeout(async () => {
      const { data, error } = await panelPost('email', 'design_render', { doc: JSON.parse(wire), subject: subject || '' });
      if (dead) return;
      if (error) { setErr(error); return; }
      setErr('');
      setPreview({ html: data.html, text: data.text, subject: data.subject, unknown: data.unknown || [] });
    }, 450);
    return () => { dead = true; clearTimeout(t); };
  }, [wire, subject]);

  const alignToggle = (b: Block) => (
    <span className="inline-flex rounded-full border border-grayscale-300 overflow-hidden text-[11px]">
      {(['left', 'center'] as const).map(a => (
        <button key={a} type="button" onClick={() => patch(b._k!, { align: a })}
          className={`px-2 py-0.5 ${(b.align || 'left') === a ? 'bg-primary-500 text-grayscale-100' : 'text-grayscale-1200'}`}>
          {a === 'left' ? 'Left' : 'Centre'}
        </button>
      ))}
    </span>
  );

  const editor = (b: Block, i: number) => {
    const k = b._k!;
    switch (b.type) {
      case 'header':
        return (
          <div className="flex flex-wrap gap-2">
            <input value={b.brand || ''} maxLength={80} onChange={e => patch(k, { brand: e.target.value })}
              placeholder="UniversityTech.io" className={`${inputCls} !w-56`} />
            <input value={b.logo || ''} onChange={e => patch(k, { logo: e.target.value })}
              placeholder="Logo image (optional, https://…png)" className={`${inputCls} flex-1 min-w-[14rem]`} />
          </div>
        );
      case 'heading':
        return (
          <div className="flex flex-wrap gap-2">
            <input value={b.text || ''} maxLength={300} onChange={e => patch(k, { text: e.target.value })}
              onFocus={inputTarget(v => patch(k, { text: v }))} className={`${inputCls} flex-1 min-w-[16rem]`} />
            <select value={b.size === 'sm' ? 'sm' : 'lg'} onChange={e => patch(k, { size: e.target.value as 'lg' | 'sm' })} className={small}>
              <option value="lg">Large</option><option value="sm">Small</option>
            </select>
          </div>
        );
      case 'text': case 'footer': case 'callout':
        return (
          <div className="flex flex-col gap-1.5">
            <RichText ref={h => { rich.current[k] = h; }} value={b.html || ''} onChange={h => patch(k, { html: h })}
              onFocus={() => { target.current = { kind: 'rich', key: k }; }}
              minHeight={b.type === 'footer' ? 50 : 90} placeholder="Write here…" />
            {b.type === 'callout' && (
              <span className="flex items-center gap-2 text-[11px] text-grayscale-1100">
                Box colour
                <Color value={b.bg || ''} onChange={v => patch(k, { bg: v })} title="Highlight box colour" palette={['#F5EFE6', '#EEF4FB', '#EEF7F0', '#FDF3E7']} />
                {b.bg && <button type="button" className="underline" onClick={() => patch(k, { bg: '' })}>Auto (from the accent)</button>}
              </span>
            )}
          </div>
        );
      case 'buttons':
        return (
          <div className="flex flex-col gap-2">
            {(b.buttons || []).map((x, bi) => (
              <div key={bi} className="flex flex-wrap items-center gap-1.5 p-2 rounded-md bg-grayscale-200/50">
                <input value={x.label} maxLength={80} onChange={e => setButton(k, bi, { label: e.target.value })}
                  onFocus={inputTarget(v => setButton(k, bi, { label: v }))}
                  placeholder="Button text" className={`${inputCls} !w-48`} />
                <input value={x.url} list="design-url-presets" onChange={e => setButton(k, bi, { url: e.target.value.trim() })}
                  onFocus={inputTarget(v => setButton(k, bi, { url: v }))}
                  placeholder="https://… or {upgrade_url}"
                  className={`${inputCls} flex-1 min-w-[12rem] ${x.url && !URL_OK.test(x.url) ? '!border-red-400' : ''}`} />
                <select value={x.style} onChange={e => setButton(k, bi, { style: e.target.value as DesignButton['style'] })} className={small}>
                  <option value="solid">Filled</option><option value="outline">Outline</option><option value="link">Text link</option>
                </select>
                <Color value={x.bg} onChange={v => setButton(k, bi, { bg: v })} title="Button colour" palette={palette} />
                {x.style === 'solid' && <Color value={x.color} onChange={v => setButton(k, bi, { color: v })} title="Button text colour" />}
                <button type="button" onClick={() => patch(k, { buttons: (b.buttons || []).filter((_, j) => j !== bi) })}
                  disabled={(b.buttons || []).length <= 1} className="text-xs text-red-700 underline disabled:opacity-40 disabled:no-underline">Remove</button>
              </div>
            ))}
            {(b.buttons || []).length < maxButtons && (
              <button type="button" className="self-start text-xs underline text-primary-700"
                onClick={() => patch(k, { buttons: [...(b.buttons || []), {
                  label: 'Explore Cosmos', url: '{site_url}', style: 'outline', bg: th.accent, color: th.accent_text }] })}>
                + Add a button
              </button>
            )}
          </div>
        );
      case 'spacer':
        return (
          <label className="flex items-center gap-2 text-xs text-grayscale-1100">
            Height <input type="range" min={4} max={80} value={Number(doc.blocks[i].size ?? 16)}
              onChange={e => patch(k, { size: Number(e.target.value) })} />
            {Number(doc.blocks[i].size ?? 16)}px
          </label>
        );
      case 'image':
        return (
          <div className="flex flex-wrap gap-2">
            <input value={b.src || ''} onChange={e => patch(k, { src: e.target.value.trim() })}
              placeholder="Image address (https://…)" className={`${inputCls} flex-1 min-w-[14rem]`} />
            <input value={b.alt || ''} onChange={e => patch(k, { alt: e.target.value })}
              placeholder="Alt text" className={`${inputCls} !w-40`} />
            <input value={b.href || ''} onChange={e => patch(k, { href: e.target.value.trim() })}
              placeholder="Link when clicked (optional)" className={`${inputCls} !w-56`} />
          </div>
        );
      default:
        return <div className="text-[11px] text-grayscale-1100">A thin line across the e-mail.</div>;
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <datalist id="design-url-presets">{URL_PRESETS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}</datalist>
      <div className="flex flex-col gap-3 min-w-0">
        {onSubject && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-grayscale-1200">Subject</span>
            <input value={subject || ''} maxLength={250} onChange={e => onSubject(e.target.value)}
              onFocus={inputTarget(v => onSubject(v))}
              placeholder="e.g. {first_name}, your free trial is waiting" className={inputCls} />
          </label>
        )}
        <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Placeholders — click to insert where you are typing (filled per person when sent)</span>
          <div className="flex flex-wrap gap-1">
            {placeholders.map(p => (
              <button key={p.name} type="button" title={p.meaning} onMouseDown={e => e.preventDefault()} onClick={() => insert(p.name)}
                className="px-2 py-0.5 rounded-full border border-primary-300 bg-primary-100/50 text-[11px] text-grayscale-1300 font-mono hover:bg-primary-100">
                {`{${p.name}}`}
              </button>
            ))}
          </div>
        </div>

        <details className="rounded-lg border border-grayscale-300 bg-white" open>
          <summary className="px-3 py-2 text-sm font-bold text-grayscale-1300 cursor-pointer">Theme</summary>
          <div className="px-3 pb-3 grid gap-2 text-xs text-grayscale-1200">
            <span className="flex items-center justify-between gap-2">Accent (buttons, links) <Color value={th.accent} onChange={v => setTheme({ accent: v })} title="Accent" palette={palette} /></span>
            <span className="flex items-center justify-between gap-2">Button text <Color value={th.accent_text} onChange={v => setTheme({ accent_text: v })} title="Button text" palette={['#FFFFFF', '#2B2B2B']} /></span>
            <span className="flex items-center justify-between gap-2">Page background <Color value={th.bg} onChange={v => setTheme({ bg: v })} title="Background" palette={['#F7F3EE', '#F0EAE1', '#FFFFFF', '#F5F5F5']} /></span>
            <span className="flex items-center justify-between gap-2">Card <Color value={th.card} onChange={v => setTheme({ card: v })} title="Card" palette={['#FFFFFF', '#FCFAF7']} /></span>
            <span className="flex items-center justify-between gap-2">Text <Color value={th.text} onChange={v => setTheme({ text: v })} title="Text" palette={['#2B2B2B', '#202124']} /></span>
            <span className="flex items-center justify-between gap-2">Muted text <Color value={th.muted} onChange={v => setTheme({ muted: v })} title="Muted" palette={['#6B6B6B', '#80653F']} /></span>
            <label className="flex items-center justify-between gap-2">Font
              <select value={th.font} onChange={e => setTheme({ font: e.target.value })} className={small}>
                {fonts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </label>
            <label className="flex items-center justify-between gap-2">Buttons
              <select value={th.radius} onChange={e => setTheme({ radius: Number(e.target.value) })} className={small}>
                {RADII.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </label>
          </div>
        </details>

        {doc.blocks.map((b, i) => (
          <div key={b._k} className="rounded-lg border border-grayscale-300 bg-white p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wide text-grayscale-1100">{i + 1}. {BLOCK_LABELS[b.type]}</span>
              <span className="flex items-center gap-2">
                {ALIGNED.includes(b.type) && alignToggle(b)}
                <button type="button" title="Move up" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-1 disabled:opacity-30">↑</button>
                <button type="button" title="Move down" onClick={() => move(i, 1)} disabled={i === doc.blocks.length - 1} className="text-xs px-1 disabled:opacity-30">↓</button>
                <button type="button" title="Duplicate" onClick={() => duplicate(i)} disabled={doc.blocks.length >= maxBlocks} className="text-xs px-1 disabled:opacity-30">Copy</button>
                <button type="button" title="Delete" onClick={() => remove(b._k!)} disabled={doc.blocks.length <= 1} className="text-xs px-1 text-red-700 disabled:opacity-30">Delete</button>
              </span>
            </div>
            {editor(b, i)}
          </div>
        ))}
        <div className="flex items-center gap-2">
          <select value={addType} onChange={e => setAddType(e.target.value as BlockType)} className={small}>
            {ADDABLE.map(t => <option key={t} value={t}>{BLOCK_LABELS[t]}</option>)}
          </select>
          <button type="button" onClick={add} disabled={doc.blocks.length >= maxBlocks}
            className="h-8 px-3 rounded-full bg-primary-500 text-grayscale-100 text-xs font-bold disabled:opacity-40">+ Add block</button>
        </div>
      </div>

      <div className="min-w-0 xl:sticky xl:top-4 self-start flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
          <span className="font-bold text-grayscale-1300">Preview <span className="font-normal text-grayscale-1100">(a sample person)</span></span>
          <span className="flex gap-1">
            {(['desktop', 'phone'] as const).map(w => (
              <button key={w} type="button" onClick={() => setWidth(w)}
                className={`px-2 py-0.5 rounded-full ${width === w ? 'bg-primary-500 text-grayscale-100' : 'border border-grayscale-300'}`}>
                {w === 'desktop' ? 'Desktop' : 'Phone'}
              </button>
            ))}
            {(['html', 'text'] as const).map(v => (
              <button key={v} type="button" onClick={() => setView(v)}
                className={`px-2 py-0.5 rounded-full ${view === v ? 'bg-primary-500 text-grayscale-100' : 'border border-grayscale-300'}`}>
                {v === 'html' ? 'HTML' : 'Plain text'}
              </button>
            ))}
          </span>
        </div>
        {err && <div className="text-xs text-red-700">{err}</div>}
        {preview?.unknown?.length ? (
          <div className="text-xs text-amber-800">
            Unknown placeholder{preview.unknown.length > 1 ? 's' : ''}: {preview.unknown.map(u => `{${u}}`).join(', ')} —
            they would be refused when sending.
          </div>
        ) : null}
        {preview && onSubject && <div className="text-xs text-grayscale-1200"><b>Subject:</b> {preview.subject || '(none)'}</div>}
        <div className="rounded-lg border border-grayscale-300 bg-grayscale-200/40 p-2 flex justify-center overflow-hidden">
          {!preview ? <div className="text-xs text-grayscale-1100 py-10">Rendering…</div> : view === 'html' ? (
            <iframe title="E-mail preview" srcDoc={preview.html} sandbox=""
              style={{ width: width === 'desktop' ? '100%' : 375, maxWidth: '100%', height: 640, border: 0, background: '#fff' }} />
          ) : (
            <pre className="w-full whitespace-pre-wrap text-xs text-grayscale-1300 bg-white p-3 rounded max-h-[640px] overflow-auto">{preview.text}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignBuilder;
